import type {
  AssessmentEntity,
  AssessmentResponseEntity,
  AssessmentStatus,
  AssessmentType,
  EvaluationAssessmentPropertiesEntity,
  SectionEntity,
} from '../../domain/entities/AssessmentEntity.js';

export interface AssessmentDbRow {
  id: string;
  org_id: string;
  project_id: string;
  title: string;
  description: string | null;
  type: string;
  status: string;
  version: number;
  sections: unknown[];
  evaluation_properties: Record<string, unknown>;
  active: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentResponseDbRow {
  id: string;
  assessment_id: string;
  respondent_id: string;
  status: string;
  answers: unknown[];
  total_score: number | null;
  required_percentage_to_pass: number;
  actual_percentage: number;
  is_passed: boolean;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export function toAssessmentEntity(row: AssessmentDbRow): AssessmentEntity {
  const evalProps = row.evaluation_properties || (row as any).evaluation_assessment_properties || {};
  const evaluationAssessmentProperties: EvaluationAssessmentPropertiesEntity = {
    passScorePercentage: (evalProps.passScorePercentage as number) ?? (evalProps.pass_score_percentage as number) ?? 100,
    limitTimeInMinutes: (evalProps.limitTimeInMinutes as number) ?? (evalProps.limit_time_in_minutes as number) ?? 0,
    maxAttempts: (evalProps.maxAttempts as number) ?? (evalProps.max_attempts as number) ?? 0,
    randomQuestionsCount: (evalProps.randomQuestionsCount as number) ?? (evalProps.random_questions_count as number) ?? 0,
    allowedRoles: (evalProps.allowedRoles as string[]) ?? (evalProps.allowed_roles as string[]) ?? [],
  };

  return {
    id: row.id,
    orgId: row.org_id,
    projectId: row.project_id,
    title: row.title,
    description: row.description ?? undefined,
    type: (row.type as AssessmentType) || 'evaluation',
    status: (row.status as AssessmentStatus) || 'draft',
    version: row.version ?? 1,
    sections: (row.sections as SectionEntity[]) || [],
    evaluationAssessmentProperties,
    active: row.active ?? true,
    publishedAt: row.published_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toAssessmentDbRow(entity: Partial<AssessmentEntity>): Partial<AssessmentDbRow> {
  const row: Partial<AssessmentDbRow> = {};
  if (entity.id !== undefined) row.id = entity.id;
  if (entity.orgId !== undefined) row.org_id = entity.orgId;
  if (entity.projectId !== undefined) row.project_id = entity.projectId;
  if (entity.title !== undefined) row.title = entity.title;
  if (entity.description !== undefined) row.description = entity.description ?? null;
  if (entity.type !== undefined) row.type = entity.type;
  if (entity.status !== undefined) row.status = entity.status;
  if (entity.version !== undefined) row.version = entity.version;
  if (entity.sections !== undefined) row.sections = entity.sections as unknown[];
  if (entity.evaluationAssessmentProperties !== undefined) {
    row.evaluation_properties = {
      passScorePercentage: entity.evaluationAssessmentProperties.passScorePercentage,
      limitTimeInMinutes: entity.evaluationAssessmentProperties.limitTimeInMinutes,
      maxAttempts: entity.evaluationAssessmentProperties.maxAttempts,
      randomQuestionsCount: entity.evaluationAssessmentProperties.randomQuestionsCount,
      allowedRoles: entity.evaluationAssessmentProperties.allowedRoles,
    };
  }
  if (entity.active !== undefined) row.active = entity.active;
  if (entity.publishedAt !== undefined) row.published_at = entity.publishedAt ?? null;
  return row;
}

export function toAssessmentResponseEntity(row: AssessmentResponseDbRow): AssessmentResponseEntity {
  return {
    id: row.id,
    assessmentId: row.assessment_id,
    respondentId: row.respondent_id,
    status: (row.status as 'draft' | 'submitted') || 'submitted',
    answers: (row.answers as any[]) || [],
    totalScore: row.total_score ?? undefined,
    requiredPercentageToPass: row.required_percentage_to_pass ?? 100,
    actualPercentage: row.actual_percentage ?? 0,
    isPassed: row.is_passed ?? false,
    submittedAt: row.submitted_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
